import MapView from '@arcgis/core/views/MapView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import './map.css';
import Search from '@arcgis/core/widgets/Search.js';
import type { PropsWithChildren } from 'react';
import React, { useState, useEffect, useRef } from 'react';

import { useActions, useAppState } from '@/overmind';
import type {
  FeaturesInLayer
} from '@/overmind/effects/adapters/baseadapter';

import { useMap } from './MapProvider';
import { useInitializeMapView } from './MapViewProvider';

import { ConfigureLevelOfDetails } from './ZoomSettings/ConfigureLevelOfDetails';
import { MIN_SCALE_AS_BIG_COUNTRIES } from './ZoomSettings/ScalesDictionary';

import { getExtentShowingAllGraphics } from './getExtent';
import { MapProps } from './MapProps';
import { useIsGridElementType } from '../Calculation/hooks/useIsGridElementType';
import { LatLng, LatLngBounds } from 'leaflet';
import type Extent from '@arcgis/core/geometry/Extent';
import { Feature } from '@/common/arcgis/Feature';


export const Map: React.FunctionComponent<
  PropsWithChildren<MapProps>
> = props => {
  const { setMapCenter } = useActions().map;
  const mapDiv = useRef(null);
  const map = useMap();
  const initializeMapView = useInitializeMapView();
  const config = useAppState().arcgis.config.value;
  const [view, setView] = useState<MapView | null>(null);
  let oldFeaturesInLayers: FeaturesInLayer[] | undefined;
  useEffect(() => {
    if (!config) return;
    if (!mapDiv.current || !map) return;
    const view = new MapView({
      map,
      container: mapDiv.current,
      popupEnabled: true,
      constraints: {
        rotationEnabled: false,
        minScale: MIN_SCALE_AS_BIG_COUNTRIES,
        maxScale: 0,
        lods: ConfigureLevelOfDetails(map)
      }
    });
    initializeMapView(view);
    view.ui.add('zoom', 'top-right');
    const searchWidget = new Search({
      view
    });
    // Adds the search widget below other elements in
    // the top left corner of the view
    view.ui.add(searchWidget, {
      position: 'top-left',
      index: 2
    });

    view.on('click', event => {
      const screenPoint = (event as unknown as __esri.HitTestResult)
        .screenPoint;
      view.hitTest(screenPoint).then(handleGraphicClick(props, screenPoint));
    });

    setView(view);
    if(props.onViewInitialized) {
      props.onViewInitialized({ view });
    }

    view.on("layerview-create", async (event) => {
      if(map.loaded) {
        const graphics = (event.layerView.layer as GraphicsLayer).graphics;
        if(graphics !== undefined && props.featuresInLayers !== oldFeaturesInLayers) {
          oldFeaturesInLayers = props.featuresInLayers;
          const extentShowingAllGraphics = getExtentShowingAllGraphics(graphics)
          view.goTo(extentShowingAllGraphics);
          const leafletBounds = esriExtentToLeafletBounds(extentShowingAllGraphics);
          setMapCenter({zoom: view.zoom, center: new LatLng(view.center.x, view.center.y, undefined), bounds: leafletBounds});
        }
      }
    });

    return () => {
      if (!view) return;
      view.map = null as any;
      view.destroy();
    };
  }, [map, initializeMapView, config]);

  const isGridElementType = useIsGridElementType();
  props.useAddCalculationSetExecutionResultsToMap(view, props, map, isGridElementType);

  useEffect(() => {
    if (!view) return;
    if (props.unselectFeature) {
      view.graphics.removeAll();
    }
  }, [props.unselectFeature, view]);

  return (
    <>
      <div id="map" ref={mapDiv} />
    </>
  );
};
export function handleGraphicClick(props: PropsWithChildren<MapProps>, screenPoint: __esri.MapViewScreenPoint): ((value: __esri.HitTestResult) => void | PromiseLike<void>) {
  return response => {
    const graphicHits = response.results as unknown as __esri.GraphicHit[];
    const globalIds = graphicHits.map(result => Feature.attributeOf(result.graphic, 'globalId'));
    const featuresInLayersCopy = props.featuresInLayers ? [...props.featuresInLayers] : undefined; // do it have to be copy?
    var featuresToPickFrom = featuresInLayersCopy?.flatMap(layer => layer.features).filter(feature => globalIds.includes(feature.gc.globalId)) ?? [];
    props.onClick({
      features: featuresToPickFrom,
      graphics: graphicHits.map(result => result.graphic),
      mapInteractionLocation: screenPoint
    });
  };
}

const esriExtentToLeafletBounds = (extent: Extent): LatLngBounds => {
  const southWest = new LatLng(extent.ymin, extent.xmin);
  const northEast = new LatLng(extent.ymax, extent.xmax);
  return new LatLngBounds(southWest, northEast);
};
